import type { LocaleResource } from './types';
import { getA11yFallback } from './a11yFallback';

const resource = {
  common: {
    loading: 'लोड हो रहा है...',
    actions: {
      close: 'मेनू बंद करें',
      search: 'खोजें',
      backHome: 'होम पर वापस जाएँ',
      login: 'लॉग इन',
      register: 'खाता बनाएँ',
      explore: 'एक्सप्लोर करें',
    },
    states: {
      noResults: 'कोई परिणाम नहीं',
      noResultsFor: '“{{query}}” के लिए कोई परिणाम नहीं।',
    },
  },
  app: {
    skipToContent: 'मुख्य सामग्री पर जाएँ',
    loading: 'लोड हो रहा है...',
    notFound: {
      message: 'यह पेज Skoleom यूनिवर्स में मौजूद नहीं है।',
      cta: 'होम पर वापस जाएँ',
    },
  },
  a11y: getA11yFallback('hi'),
  header: {
    brandHome: 'होम',
    universe: 'Universe',
    navLabel: 'मुख्य नेविगेशन',
    searchPlaceholder: 'खोजें',
    searchAria: 'खोजें',
    language: {
      change: 'भाषा बदलें',
      zones: {
        international: 'अंतरराष्ट्रीय',
        europe: 'यूरोप और यूके',
        americas: 'अमेरिका',
        mena: 'MENA',
        asiaPacific: 'एशिया-प्रशांत',
        africa: 'अफ्रीका',
      },
    },
    actions: {
      settings: 'सेटिंग्स',
      favorites: 'पसंदीदा',
      cart: 'कार्ट',
      account: 'उपयोगकर्ता खाता',
      openMenu: 'मेनू खोलें',
      closeMenu: 'मेनू बंद करें',
    },
    account: {
      dashboard: 'मेरा डैशबोर्ड',
      orders: 'मेरे ऑर्डर',
      favorites: 'पसंदीदा',
      admin: 'एडमिन कंसोल',
      adminBadge: 'Admin',
      logout: 'लॉग आउट',
    },
    nav: {
      home: 'होम',
      stores: 'दुकानें',
      music: 'संगीत',
      subscriptions: 'सदस्यताएँ',
      everyone: 'सभी के लिए',
      pros: 'पेशेवरों के लिए',
      news: 'समाचार',
      events: 'इवेंट',
      group: 'ग्रुप',
      support: 'सहायता',
    },
  },
  cart: {
    title: 'मेरा कार्ट',
    itemCount: '{{count}} आइटम',
    empty: {
      title: 'आपका कार्ट खाली है',
      subtitle: 'हमारी खरीद योग्य सामग्री खोजें',
    },
    summary: {
      subtotal: 'सबटोटल',
      shipping: 'शिपिंग',
      free: 'मुफ़्त',
      total: 'कुल',
    },
    checkout: {
      cta: 'Skoleom Pay से भुगतान करें',
      security: 'सुरक्षित सार्वभौमिक भुगतान · GDPR अनुरूप',
    },
    aria: {
      decrease: 'मात्रा घटाएँ',
      increase: 'मात्रा बढ़ाएँ',
      remove: 'यह आइटम हटाएँ',
      close: 'कार्ट बंद करें',
    },
  },
  skoleomPage: {
    nav: {
      ariaLabel: 'Skoleom Page नेविगेशन',
      home: 'होम',
      homeMobile: 'होम',
      trends: 'ट्रेंड',
      trendsMobile: 'ट्रेंड',
      create: 'अपना SeContent बनाएं',
      createMobile: 'बनाएं',
      profile: 'प्रोफ़ाइल',
      profileMobile: 'प्रोफ़ाइल',
    },
    explorer: {
      poweredBy: 'SeSync द्वारा संचालित',
      heroBefore: 'देखें। क्लिक करें। ',
      heroHighlight: 'खोजें।',
      heroSubtitle:
        'एक खोज, पूरा Skoleom Universe इकोसिस्टम — वीडियो, संगीत, मीडिया, शॉपिंग। हर परिणाम एक इमर्सिव प्रवेश द्वार है।',
      logoAlt: 'Skoleom Page',
      searchPlaceholder: 'कलाकार, फ़िल्म, ब्रांड खोजें…',
      searchAria: 'खोज शब्द',
      searchSubmit: 'खोज शुरू करें',
      searchReset: 'खोज रीसेट करें',
      exploreCta: 'खोजें',
      resultsFor: 'इसके परिणाम',
      resultsOnPlatforms_one: '{{count}} प्लेटफ़ॉर्म पर',
      resultsOnPlatforms_other: '{{count}} प्लेटफ़ॉर्म पर',
      filter: 'फ़िल्टर',
      platforms: 'प्लेटफ़ॉर्म',
      filterPlatformsAria: 'प्लेटफ़ॉर्म फ़िल्टर करें',
      selectAll: 'सभी',
      selectNone: 'कोई नहीं',
      loading: 'Skoleom ब्रह्मांड से कनेक्ट हो रहा है…',
      noResults: 'आपके वर्तमान फ़िल्टर से कोई परिणाम मेल नहीं खाता। अधिक प्लेटफ़ॉर्म चुनें।',
    },
    actionCards: {
      sectionBefore: 'खोजें ',
      sectionHighlight: 'Skoleom ब्रह्मांड',
      mobile: {
        title: 'मोबाइल',
        description: 'iOS और Android पर उपलब्ध।',
      },
      tv: {
        title: 'TV',
        description: 'अपने कनेक्टेड TV पर देखें।',
      },
      rewards: {
        title: 'पुरस्कार',
        description: 'प्रीमियम खरीद पर कैशबैक।',
      },
      extension: {
        title: 'एक्सटेंशन',
        description: 'सभी सुविधाओं तक पहुँचें।',
      },
      audio: {
        title: 'ऑडियो',
        description: 'सभी उपकरणों पर सुनें।',
      },
      gaming: {
        title: 'गेमिंग',
        description: 'हमारे गेम ऐप खोजें।',
      },
    },
  },
  footer: {
    brand: {
      description:
        'Skoleom इकोसिस्टम ऑडियोविज़ुअल स्टोर, क्रिएटर टूल और बिजनेस समाधान को साथ लाता है ताकि सामग्री इंटरैक्टिव और कार्रवाई योग्य बने।',
    },
    socials: {
      label: 'Skoleom सोशल नेटवर्क',
    },
    shopPrompt: {
      prefix: 'आप खरीदारी यहाँ भी कर सकते हैं',
      link: 'Skoleom ऑनलाइन स्टोर',
    },
    copyright: 'सर्वाधिकार सुरक्षित।',
    modal: {
      close: 'मेनू बंद करें',
      title: 'सेवा उपलब्ध नहीं',
      body: 'यह सेवा आपके क्षेत्र में उपलब्ध नहीं है।',
    },
    columns: {
      about: 'Skoleom के बारे में',
      stores: 'ऑडियोविज़ुअल स्टोर',
      everyone: 'सभी के लिए',
      pros: 'पेशेवरों के लिए',
      group: 'ग्रुप',
      '1': 'Skoleom के बारे में',
      '2': 'ऑडियोविज़ुअल स्टोर',
      '3': 'सभी के लिए',
      '4': 'पेशेवरों के लिए',
      '5': 'ग्रुप',
    },
    links: {
      '101': 'हमारा मिशन',
      '102': 'हमारी तकनीक',
      '103': 'Skoleom इकोसिस्टम',
      '104': 'फंडिंग कार्यक्रम',
      '105': 'Skoleom पेटेंट',
      '201': 'स्टोर एक्सप्लोर करें',
      '202': 'श्रेणी के अनुसार यूनिवर्स',
      '203': 'स्टोर बनाएँ',
      '301': 'यह कैसे काम करता है',
      '302': 'देखें और खरीदें',
      '303': 'कैशबैक और पुरस्कार',
      '304': 'Skoleom Wallet',
      '305': 'सुरक्षा और गोपनीयता',
      '401': 'बिजनेस समाधान',
      '402': 'अपनी सामग्री से कमाई करें',
      '403': 'ऑडियोविज़ुअल स्टोर बनाएँ',
      '404': 'अपने उत्पाद वितरित करें',
      '405': 'इंटरैक्टिव विज्ञापन',
      '406': 'API और इंटीग्रेशन',
      '407': 'Audiovisual Store Page बनाएँ',
      '501': 'समाचार',
      '502': 'करियर',
      '503': 'निवेशक',
      '504': 'प्रेस',
      '505': 'संपर्क',
      '506': 'सहायता',
    },
    legalLinks: {
      '901': 'कानूनी सूचना',
      '902': 'उपयोग और बिक्री की शर्तें',
      '903': 'गोपनीयता',
      '904': 'एफिलिएट प्रकटीकरण',
      '905': 'कुकी प्राथमिकताएँ',
      '906': 'रिफंड नीति',
    },
  },
  auth: {
    login: {
      title: 'लॉग इन',
      submit: 'लॉग इन',
    },
    register: {
      title: 'खाता बनाएँ',
      submit: 'खाता बनाएँ',
    },
  },
  dashboard: {
    user: {
      orders: 'मेरे ऑर्डर',
    },
    admin: {
      title: 'एडमिन कंसोल',
    },
  },
  home: {
    hero: {
      title: 'Skoleom Group का वैश्विक इकोसिस्टम खोजें',
      description: {
        intro: 'Skoleom Group ',
        economy: 'डिजिटल अर्थव्यवस्था',
        mid: ' में क्रांति ला रहा है, ऑडियोविज़ुअल सामग्री के केंद्र में त्वरित खरीदारी सक्षम करके। ',
        ott: '2000+ OTT प्लेटफ़ॉर्म',
        join: ' और ',
        web: 'अरबों वेबसाइटों',
        outro: ' से जुड़ा, हमारी तकनीक हर स्क्रीन को इंटरैक्टिव बिक्री बिंदु बनाती है।',
      },
      download: 'डाउनलोड करें',
      application: 'ऐप',
      extension: 'एक्सटेंशन',
      watchVideo: 'वीडियो देखें',
      tagline1: 'हर वीडियो एक सच्चा इमर्सिव स्टोर बन जाता है:',
      tagline2:
        'एक सहज, सुरुचिपूर्ण और इंटरैक्टिव अनुभव, जहाँ आप जो देखते हैं उसे तुरंत खरीदते हैं।',
    },
    actions: {
      discoverMagic: 'जादू खोजें',
    },
    welcome: {
      part1: 'रिटेल मीडिया और ट्रांसमीडिया के ',
      part2: 'भविष्य में ',
      part3: 'आपका स्वागत',
      part4: ' है — ',
      part5: '',
    },
    page: {
      heroClose: 'बंद करें',
      interactiveKicker: 'एक इंटरैक्टिव अनुभव',
      interactiveSubtitle: 'वीडियो के अंदर।\nउसी पल।\nबिना रुकावट।',
      ecosystemKicker: 'एक ब्रह्मांड। कई संभावनाएँ।',
      ecosystemTitle: 'Skoleom\nइकोसिस्टम',
      partnersTitle: 'पार्टनर ब्रांड',
      boutiquesCta: 'हमारी',
      boutiquesTitle: 'ऑडियोविज़ुअल स्टोर खोजें',
      boutiquesSubtitle: 'एक इमर्सिव इकोसिस्टम। 50+ वेब ऐप। एक सीमा: आपकी कल्पना।',
    },
    news: {
      badge: 'नया',
    },
    boutiques: {
      title: 'हमारे ऑडियोविज़ुअल स्टोर खोजें',
      searchPlaceholder: 'Skoleom साइट खोजें (जैसे: market, sport, ott...)',
    },
    sections: {
      welcome: 'रिटेल मीडिया और ट्रांसमीडिया के भविष्य में आपका स्वागत है',
      how: 'Skoleom कैसे काम करता है',
      moment: 'हर पल। बन जाता है। एक अवसर।',
      interactive: 'एक इंटरैक्टिव अनुभव',
      universe: 'एक ब्रह्मांड। कई संभावनाएँ।',
      ecosystem: 'Skoleom इकोसिस्टम',
      partners: 'पार्टनर ब्रांड',
    },
    descriptions: {
      detect:
        'Skoleom आप जो देखते हैं उसे पहचानता है और जो कुछ भी आप देखते हैं उसे खरीदने योग्य बनाता है — एक अभूतपूर्व इंटरैक्टिव अनुभव।',
    },
    stats: {
      storePages: 'प्रति दुकान Audiovisual Store पेज।',
    },
  },
  public: {
    stores: {
      hero: {
        titleLine1: 'हमारी',
        titleLine2: 'ऑडियोविज़ुअल स्टोर खोजें',
        subtitle: 'एक इमर्सिव इकोसिस्टम। 50+ वेब ऐप। एक सीमा: आपकी कल्पना।',
      },
      search: {
        placeholder: 'स्टोर या ब्रांड खोजें',
        aria: 'स्टोर या ब्रांड खोजें',
      },
      sections: {
        official: 'आधिकारिक स्टोर',
        brands: 'उपलब्ध ब्रांड',
      },
      showcase: {
        kicker: 'फ़ीचर्ड स्टोर',
        title: 'प्रमुख यूनिवर्स',
        subtitle: 'स्पोर्ट, OTT, संगीत और गेमिंग — प्रतिष्ठित ऑडियोविज़ुअल स्टोर।',
      },
      carousel: {
        kicker: 'सभी स्टोर',
        title: 'ऑडियोविज़ुअल स्टोर',
        subtitle: 'Skoleom इकोसिस्टम में सभी स्टोर खोजें।',
        searchPlaceholder: 'स्टोर खोजें (जैसे: sport, music, games...)',
        ariaLabel: 'ऑडियोविज़ुअल स्टोर कैरोसेल',
      },
    },
    howItWorks: {
      hero: {
        kicker: 'यह कैसे काम करता है',
        subtitle: 'वीडियो के अंदर। उसी पल। बिना रुकावट।',
      },
      steps: {
        '01': {
          title: 'AI पहचान',
          desc: 'हमारी AI किसी भी कंटेंट में उत्पाद, वस्तुएँ और पल पहचानती है।',
        },
        '02': {
          title: 'स्मार्ट कैप्सूल',
          desc: 'इंटरैक्टिव कैप्सूल रियल टाइम में, बिना रुकावट।',
        },
        '03': {
          title: 'एक टैप',
          desc: 'एक क्लिक। कोई रीडायरेक्ट नहीं। कोई घर्षण नहीं। तुरंत कार्रवाई।',
        },
        '04': {
          title: 'संदर्भ में खरीद',
          desc: 'पसंदीदा कंटेंट छोड़े बिना खरीद पूरी करें।',
        },
      },
      demo: {
        titlePrefix: 'खोजें',
        titleHighlight: 'जादू',
        cta: 'इंटरैक्टिव डेमो शुरू करें',
      },
      showcase: {
        kicker: 'उपभोक्ता यूनिवर्स',
        title: 'फ़ीचर्ड स्टोर',
        subtitle: 'सभी के लिए इंटरैक्टिव अनुभव, आपके पसंदीदा कंटेंट में एकीकृत।',
      },
      carousel: {
        kicker: 'सभी स्टोर',
        title: 'सभी के लिए',
        subtitle: 'Skoleom इकोसिस्टम के सभी उपभोक्ता स्टोर।',
        searchPlaceholder: 'खोजें (जैसे: page, shop, magazine...)',
        ariaLabel: 'सभी के लिए स्टोर कैरोसेल',
      },
    },
    technology: {
      hero: {
        kicker: 'SeSync · तकनीक',
        titlePrefix: 'से अधिक से जुड़ा',
        titleMiddle: 'और',
      },
      features: {
        seamless: 'वीडियो में सीधे सहज खरीदारी अनुभव',
        compatible: 'आपके पसंदीदा ब्रांड और क्रिएटर के साथ संगत',
        secure: 'सुरक्षित और GDPR अनुपालन',
      },
      download: 'एक्सटेंशन डाउनलोड करें',
      studio: {
        titleLine1: 'मुद्रीकरण उपकरण',
        titleLine2Prefix: 'दुनिया का सबसे',
        titleLine2Highlight: 'शक्तिशाली',
        description:
          'इंटरैक्टिव वीडियो कंटेंट प्रबंधन, मुद्रीकरण और विश्लेषण के लिए ERP/DCM SaaS। 300+ APIs से जुड़ा। पूर्ण नियंत्रण।',
      },
    },
  },
  data: {
    showcase: {
      'av-sesports': {
        title: 'Skoleom SeSports',
        desc: 'Interactive sports universe: video-to-commerce, retail media and fan engagement. The group official audiovisual store for sports brands and federations.',
      },
      'av-watch': {
        title: 'Watch on Skoleom',
        desc: 'Skoleom shoppable OTT experience: movies, series and live streams where every visible product becomes accessible in one tap.',
      },
      'av-music': {
        title: 'Skoleom Music',
        desc: 'Label, artists and merchandising. The audiovisual store dedicated to music with direct purchase capsules in clips and concerts.',
      },
      'av-games': {
        title: 'Skoleom Games',
        desc: 'Gaming universe and in-game monetization. Proprietary IP, studio partnerships and interactive capsules embedded in content.',
      },
      'tous-sesync': {
        title: 'SeSync',
        desc: 'Skoleom synchronization engine: in-page purchasing and real-time multi-platform distribution.',
      },
      'tous-shop': {
        title: 'Skoleom Shop',
        desc: 'The group official marketplace: products, merchandise and limited editions available directly from your favorite content.',
      },
      'tous-secontent': {
        title: 'SeContent Creation',
        desc: 'Creation and orchestration of interactive capsules. Intuitive tools to turn any content into a shoppable experience.',
      },
      'tous-magazine': {
        title: 'Skoleom Magazine',
        desc: 'Media and interactive formats: news, features and reports enriched with inline shopping capsules.',
      },
    },
    boutiques: {
      'av-sesports': {
        name: 'Skoleom SeSports',
        description: 'Sports universe and retail media.',
      },
      'av-cares': {
        name: 'Skoleom Cares',
        description: 'Social impact and dedicated programs.',
      },
      'av-games': {
        name: 'Skoleom Games',
        description: 'Games, IP and in-game monetization.',
      },
      'av-watch': {
        name: 'Watch on Skoleom',
        description: 'Shoppable OTT content and live experiences.',
      },
      'av-music': {
        name: 'Skoleom Music',
        description: 'Music, artists and direct merchandising.',
      },
      'tous-page': {
        name: 'Skoleom Page',
        description: 'Skoleom entry point and discovery.',
      },
      'tous-sesync': {
        name: 'SeSync',
        description: 'In-page purchasing and distribution.',
      },
      'tous-shop': {
        name: 'Skoleom Shop',
        description: 'Official marketplace for the ecosystem.',
      },
      'tous-secontent': {
        name: 'SeContent Creation',
        description: 'Create and orchestrate interactive capsules.',
      },
      'tous-magazine': {
        name: 'Skoleom Magazine',
        description: 'Interactive media, news and reports.',
      },
    },
  },
} satisfies LocaleResource;

export default resource;
